<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Clients</h1>
        <p class="page-subtitle">{{ rows.length }} client{{ rows.length > 1 ? 's' : '' }}<span v-if="totalBalance > 0"> — Solde total: {{ fmt(totalBalance) }} HTG</span></p>
      </div>
      <v-btn color="primary" depressed @click="openNew">
        <v-icon left small>mdi-plus</v-icon>
        Nouveau client
      </v-btn>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <!-- Quick filter + search -->
      <div class="d-flex justify-space-between align-center mb-3 flex-wrap" style="gap: 8px;">
        <v-chip-group v-model="filter" mandatory active-class="primary--text">
          <v-chip small outlined>Tous ({{ rows.length }})</v-chip>
          <v-chip small outlined color="error">Avec solde ({{ withBalanceCount }})</v-chip>
        </v-chip-group>
        <input v-model="search" type="text" placeholder="Rechercher..." class="search-input" />
      </div>

      <!-- Table -->
      <v-card flat class="table-card">
        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-left">NOM</th>
              <th class="text-left">TÉLÉPHONE</th>
              <th class="text-right">SOLDE</th>
              <th class="text-right">CRÉDIT DISPO.</th>
              <th class="text-right">LIMITE</th>
              <th class="text-center">RÉDUCTION</th>
              <th class="text-center">STATUT</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in filteredRows" :key="c.id">
              <td class="text-left font-weight-medium">{{ c.full_name }}</td>
              <td class="text-left text-caption">{{ c.phone || '—' }}</td>
              <td class="text-right">
                <span :class="c.total_balance_htg > 0 ? 'error--text font-weight-bold' : 'success--text'">
                  {{ fmt(c.total_balance_htg || 0) }}
                </span>
              </td>
              <td class="text-right text-caption">{{ fmt(c.available_credit || 0) }}</td>
              <td class="text-right text-caption">{{ fmt(c.credit_limit || 0) }}</td>
              <td class="text-center">{{ c.discount || 0 }}%</td>
              <td class="text-center">
                <v-chip x-small :color="c.status ? 'success' : 'error'" dark>{{ c.status ? 'Actif' : 'Inactif' }}</v-chip>
              </td>
              <td class="text-right" style="white-space:nowrap;">
                <v-btn icon x-small @click="edit(c)" title="Modifier" class="mr-1" v-if="c.id != 1">
                  <v-icon small color="primary">mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon x-small @click="$router.push({ name: 'customer-statements', params: { id: c.id } })" title="Relevé de compte">
                  <v-icon small color="secondary">mdi-file-document-outline</v-icon>
                </v-btn>
              </td>
            </tr>
            <tr v-if="filteredRows.length === 0">
              <td colspan="8" class="text-center text--secondary pa-6">Aucun client trouvé</td>
            </tr>
          </tbody>
          <tfoot v-if="filteredRows.length > 0">
            <tr class="totals-foot">
              <td class="font-weight-bold" colspan="2">TOTAL</td>
              <td class="text-right font-weight-bold error--text">{{ fmt(filteredTotalBalance) }}</td>
              <td colspan="5"></td>
            </tr>
          </tfoot>
        </v-simple-table>
      </v-card>
    </div>

    <!-- Create/Edit dialog -->
    <v-dialog v-model="dialog" max-width="500" @click:outside="close">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="primary" size="18" class="mr-2">{{ index > -1 ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          {{ form_title }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <div class="form-group">
            <label class="form-label">Nom complet *</label>
            <v-text-field v-model="customer.full_name" outlined dense hide-details="auto" :rules="[v => !!v || 'Requis']" placeholder="Nom du client"></v-text-field>
          </div>
          <v-row>
            <v-col cols="6">
              <div class="form-group">
                <label class="form-label">Téléphone *</label>
                <v-text-field v-model="customer.phone" outlined dense hide-details="auto" :rules="[v => !!v || 'Requis']" placeholder="509..."></v-text-field>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="form-group">
                <label class="form-label">Limite de crédit</label>
                <v-text-field v-model.number="customer.credit_limit" type="number" outlined dense hide-details placeholder="0"></v-text-field>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <div class="form-group">
                <label class="form-label">Réduction (%)</label>
                <v-text-field v-model.number="customer.discount" type="number" min="0" max="30" outlined dense hide-details placeholder="0"></v-text-field>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="form-group">
                <label class="form-label">Statut</label>
                <v-select v-model="customer.status" :items="statusOptions" item-text="name" item-value="id" outlined dense hide-details></v-select>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="close">Annuler</v-btn>
          <v-btn color="primary" depressed :loading="loading" :disabled="loading || !customer.full_name || !customer.phone" @click="save">
            {{ index > -1 ? 'Enregistrer' : 'Créer' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <FlashMessage :position="'right bottom'"></FlashMessage>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'

export default {
  name: 'CustomersView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    search: '',
    filter: 0,
    index: -1,
    store_id: 0,
    loading: false,
    statusOptions: [{ name: 'Actif', id: true }, { name: 'Inactif', id: false }],
    form_title: 'Nouveau Client',
    items: [{ text: 'Clients', disabled: false, href: '/customers' }],
    enable: false,
    rows: [],
    dialog: false,
    customer: { full_name: '', phone: '', store_id: 0, status: true, credit_limit: 0, discount: 0 },
  }),
  computed: {
    totalBalance() { return this.rows.reduce(function (s, r) { return s + (parseFloat(r.total_balance_htg) || 0) }, 0) },
    withBalanceCount() { return this.rows.filter(function (r) { return parseFloat(r.total_balance_htg) > 0 }).length },
    filteredRows() {
      var list = this.rows
      if (this.filter === 1) list = list.filter(function (r) { return parseFloat(r.total_balance_htg) > 0 })
      if (!this.search.trim()) return list
      var q = this.search.toLowerCase()
      return list.filter(function (r) {
        return (r.full_name && r.full_name.toLowerCase().indexOf(q) !== -1) ||
               (r.phone && r.phone.toLowerCase().indexOf(q) !== -1)
      })
    },
    filteredTotalBalance() { return this.filteredRows.reduce(function (s, r) { return s + (parseFloat(r.total_balance_htg) || 0) }, 0) },
  },
  methods: {
    fmt(v) {
      if (!v && v !== 0) return '0.00'
      var p = Math.abs(parseFloat(v)).toFixed(2).split('.')
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return (parseFloat(v) < 0 ? '-' : '') + p.join('.')
    },
    load() {
      var self = this
      this.$axios.get('/customers?orderasc=full_name')
        .then(function (r) { self.rows = r.data; self.enable = true })
    },
    openNew() { this.reset(); this.dialog = true },
    save() {
      if (!this.customer.full_name || !this.customer.phone) return
      var self = this; this.loading = true; this.customer.store_id = this.store_id
      if (this.index > -1) {
        this.$axios.put('/customers/' + this.customer.id + '?data=' + encodeURIComponent(JSON.stringify(self.customer)))
          .then(function (r) { Object.assign(self.rows[self.index], r.data); self.dialog = false; self.loading = false; self.flashMessage.success({ title: 'Succès', message: 'Client modifié' }); self.reset() })
          .catch(function () { self.loading = false })
      } else {
        this.$axios.post('/customers?data=' + encodeURIComponent(JSON.stringify(self.customer)))
          .then(function (r) { self.rows.push(r.data); self.dialog = false; self.loading = false; self.flashMessage.success({ title: 'Succès', message: 'Client créé' }); self.reset() })
          .catch(function () { self.loading = false })
      }
    },
    edit(item) { this.index = this.rows.indexOf(item); this.customer = Object.assign({}, item); this.form_title = 'Modifier : ' + this.customer.full_name; this.dialog = true },
    close() { this.dialog = false; this.reset(); this.loading = false },
    reset() { this.customer = { full_name: '', phone: '', store_id: 0, status: true, credit_limit: 0, discount: 0 }; this.index = -1; this.form_title = 'Nouveau Client' },
    getStores() {
      this.business_id = JSON.parse(localStorage.business).id
      var self = this
      this.$axios.get('/stores?orderasc=name&business_id=' + this.business_id).then(function (r) { self.store_id = r.data[0].id })
    },
  },
  mounted() { this.load(); this.getStores() },
}
</script>

<style scoped>
.search-input { width: 260px; padding: 8px 12px; font-size: 0.8125rem; border: 1px solid #cbd5e1; border-radius: 6px; outline: none; color: #1a2332; background: #fff; }
.search-input:focus { border-color: #194173; box-shadow: 0 0 0 2px rgba(25, 65, 115, 0.1); }
.search-input::placeholder { color: #94a3b8; }
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; overflow: hidden; }
.table-card .v-simple-table thead th { font-size: 0.6875rem !important; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8 !important; background: transparent !important; border-bottom: 1px solid #e8ecf1 !important; padding: 8px 16px !important; }
.table-card .v-simple-table tbody td { font-size: 0.8125rem; padding: 10px 16px !important; border-bottom: 1px solid #f1f5f9 !important; }
.table-card .v-simple-table tbody tr:last-child td { border-bottom: none !important; }
.table-card .v-simple-table tbody tr:hover { background: rgba(25, 65, 115, 0.03) !important; }
.totals-foot td { background: #f1f5f9 !important; border-top: 2px solid #cbd5e1 !important; padding: 10px 16px !important; font-size: 0.8125rem; }
.dialog-card { border-radius: 12px !important; }
.dialog-title { font-size: 0.9375rem !important; font-weight: 600 !important; padding: 14px 20px 12px !important; }
.dialog-body { padding: 20px 20px 8px !important; }
.dialog-actions { padding: 8px 16px 12px !important; }
.form-group { margin-bottom: 16px; }
.form-label { display: block; font-size: 0.75rem; font-weight: 600; color: #475569; margin-bottom: 4px; }
</style>
